/**
 * 手机 (≤767px)：横向 overflow 容器 + rAF 推进 scrollLeft，与手指滑动兼容；用双轨宽度做无缝回绕。
 * 桌面仍用 CSS transform 跑马灯。遵守 prefers-reduced-motion。
 *
 * 性能关键（勿回退）：
 * 1. IntersectionObserver 门控——区块不在视口内绝不跑 rAF。此前从 DCL 起就每帧写 scrollLeft，
 *    即使用户停留在首屏也会持续触发 layout + 惊醒 lazysizes 的 scroll 监听，Lighthouse 实测
 *    吃掉 10s+ 主线程并把 LCP 的 render delay 拖到 30s 级。
 * 2. 帧循环内不读 getComputedStyle/offsetWidth——宽度与时长缓存，仅 resize 时重算，避免每帧强制重排。
 */
(function () {
  var MOBILE_MQ = '(max-width: 767px)';
  var REDUCE_MQ = '(prefers-reduced-motion: reduce)';

  function parseDurationSec(el) {
    var v = getComputedStyle(el).getPropertyValue('--marquee-duration').trim();
    var n = parseFloat(v);
    return n > 0 ? n : 45;
  }

  function initMarquee(marquee) {
    if (marquee.classList.contains('customer-testimonial-marquee--static')) return null;

    var viewport = marquee.querySelector('.customer-testimonial-marquee__viewport');
    var track = marquee.querySelector('.customer-testimonial-marquee__track');
    if (!viewport || !track) return null;

    // 无缝回绕需要两份轨道；第二份不在 Liquid 输出（省 ~38KB HTML），这里克隆。
    // CSS 桌面动画以 --cloned class 为启动条件，避免单轨道时 translateX(-50%) 露白。
    var groups = track.querySelectorAll('.customer-testimonial-marquee__group');
    if (groups.length === 1) {
      var dup = groups[0].cloneNode(true);
      dup.setAttribute('aria-hidden', 'true');
      track.appendChild(dup);
    }
    marquee.classList.add('customer-testimonial-marquee--cloned');

    var mqMobile = window.matchMedia(MOBILE_MQ);
    var mqReduce = window.matchMedia(REDUCE_MQ);

    var rafId = 0;
    var userPause = false;
    var resumeTimer = null;
    var touchActive = false;
    var touchStartX = 0;
    var touchStartY = 0;
    var inView = false;

    // 缓存的几何/配置值：帧循环内只读这两个变量，避免每帧强制 layout
    var cachedWidth = 0;
    var cachedSpeed = 0;

    function measure() {
      var g = track.querySelector('.customer-testimonial-marquee__group');
      cachedWidth = g ? g.offsetWidth : 0;
      if (cachedWidth > 0) {
        var sec = parseDurationSec(marquee);
        cachedSpeed = cachedWidth / (sec * 60);
        if (cachedSpeed < 0.25) cachedSpeed = 0.25;
      } else {
        cachedSpeed = 0;
      }
    }

    function loop() {
      rafId = 0;
      if (!inView || !mqMobile.matches || mqReduce.matches || document.hidden) {
        return;
      }

      if (!userPause && cachedWidth > 0) {
        viewport.scrollLeft += cachedSpeed;
        if (viewport.scrollLeft >= cachedWidth - 1) {
          viewport.scrollLeft -= cachedWidth;
        }
      }

      rafId = window.requestAnimationFrame(loop);
    }

    function startLoop() {
      if (rafId) return;
      if (cachedWidth <= 0) measure();
      rafId = window.requestAnimationFrame(loop);
    }

    function stopLoop() {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
        rafId = 0;
      }
    }

    function syncLoop() {
      if (inView && mqMobile.matches && !mqReduce.matches) {
        startLoop();
      } else {
        stopLoop();
      }
    }

    var resizeTimer = null;
    function onResize() {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        resizeTimer = null;
        cachedWidth = 0;
        if (inView && mqMobile.matches) measure();
      }, 150);
    }
    window.addEventListener('resize', onResize, { passive: true });

    // 视口门控：不可见时完全停摆（含桌面 CSS 动画，统一加 paused class 省 compositor 开销）
    var io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        inView = entries[entries.length - 1].isIntersecting;
        marquee.classList.toggle('customer-testimonial-marquee--paused', !inView);
        syncLoop();
      }, { rootMargin: '100px 0px' });
      io.observe(marquee);
    } else {
      inView = true;
    }

    function scheduleResume() {
      if (resumeTimer) window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(function () {
        userPause = false;
        resumeTimer = null;
      }, 1600);
    }

    /**
     * 不要用 scroll 事件区分「程序 vs 用户」：移动端 WebKit 常在赋值 scrollLeft 之后才异步触发 scroll，
     * scrollFromRaf 早已为 false，会每帧误判为用户滑动导致永久 userPause。
     * 只在 touchmove 上识别「明显横向拖」时暂停；纵向滑页不误伤。
     */
    function onTouchStart(e) {
      if (!mqMobile.matches || e.touches.length !== 1) return;
      touchActive = true;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }

    function onTouchMove(e) {
      if (!touchActive || !mqMobile.matches || !e.touches.length) return;
      var dx = e.touches[0].clientX - touchStartX;
      var dy = e.touches[0].clientY - touchStartY;
      if (Math.abs(dx) > Math.abs(dy) + 10 && Math.abs(dx) > 14) {
        userPause = true;
        if (resumeTimer) {
          window.clearTimeout(resumeTimer);
          resumeTimer = null;
        }
      }
    }

    function onTouchEnd() {
      touchActive = false;
      scheduleResume();
    }

    viewport.addEventListener('touchstart', onTouchStart, { passive: true });
    viewport.addEventListener('touchmove', onTouchMove, { passive: true });
    viewport.addEventListener('touchend', onTouchEnd, { passive: true });
    viewport.addEventListener('touchcancel', onTouchEnd, { passive: true });

    function onVisibility() {
      if (document.hidden) {
        stopLoop();
      } else {
        syncLoop();
      }
    }

    document.addEventListener('visibilitychange', onVisibility);
    mqMobile.addEventListener('change', syncLoop);
    mqReduce.addEventListener('change', syncLoop);

    syncLoop();

    return function destroy() {
      stopLoop();
      if (resumeTimer) window.clearTimeout(resumeTimer);
      if (resizeTimer) window.clearTimeout(resizeTimer);
      if (io) io.disconnect();
      window.removeEventListener('resize', onResize);
      viewport.removeEventListener('touchstart', onTouchStart);
      viewport.removeEventListener('touchmove', onTouchMove);
      viewport.removeEventListener('touchend', onTouchEnd);
      viewport.removeEventListener('touchcancel', onTouchEnd);
      document.removeEventListener('visibilitychange', onVisibility);
      mqMobile.removeEventListener('change', syncLoop);
      mqReduce.removeEventListener('change', syncLoop);
    };
  }

  var cleanups = new WeakMap();

  function bootMarquee(marquee) {
    if (cleanups.has(marquee)) return;
    var teardown = initMarquee(marquee);
    if (teardown) cleanups.set(marquee, teardown);
  }

  function boot(root) {
    var el = root && root.nodeType ? root : document;
    if (el.matches && el.matches('.customer-testimonial-marquee:not(.customer-testimonial-marquee--static)')) {
      bootMarquee(el);
    }
    if (el.querySelectorAll) {
      el.querySelectorAll('.customer-testimonial-marquee:not(.customer-testimonial-marquee--static)').forEach(bootMarquee);
    }
  }

  function teardownSection(sectionEl) {
    if (!sectionEl || !sectionEl.querySelectorAll) return;
    sectionEl.querySelectorAll('.customer-testimonial-marquee').forEach(function (m) {
      var fn = cleanups.get(m);
      if (fn) {
        fn();
        cleanups.delete(m);
      }
    });
  }

  function onReady() {
    boot(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) boot(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    if (event.target) teardownSection(event.target);
  });
})();
